import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  where,
  writeBatch,
} from 'firebase/firestore';
import { getDownloadURL, ref as storageRef, uploadBytes } from 'firebase/storage';
import { db, storage, useFirestore } from '../lib/firebase';
import { DUE_RANK } from '../types/models';
import type {
  Client,
  ClientWithTasks,
  Task,
  TaskAttachment,
  TriageItem,
} from '../types/models';
import { seedClients, seedTasks, seedTriage } from '../data/seed';

export interface StudioData {
  clients: ClientWithTasks[];
  triage: TriageItem[];
  loading: boolean;
  toggleTask: (taskId: string) => void;
  addTask: (clientId: string, title: string) => void;
  captureTriage: (title: string) => void;
  /** Dismiss a triage item without assigning it (remove from the inbox). */
  dismissTriage: (itemId: string) => void;
  /** Assign a triage item to a client (creating a task) and resolve it. */
  assignTriageToClient: (
    itemId: string,
    clientId: string,
    projectId: string,
    title: string,
  ) => void;
  /** Persist a new client ordering (array of client ids, top-to-bottom). */
  reorderClients: (orderedIds: string[]) => void;
  /** Patch fields on a task. */
  updateTask: (taskId: string, patch: Partial<Task>) => void;
  /** Add/remove a task from the transient Lineup. */
  toggleLineup: (taskId: string) => void;
  /** Persist a new Lineup ordering (array of task ids, top-to-bottom). */
  reorderLineup: (orderedTaskIds: string[]) => void;
  /** Empty the Lineup (unset it on every task). */
  clearLineup: () => void;
  /** Remove a task. */
  deleteTask: (taskId: string) => void;
  /** Patch fields on a client (info, projects, logoUrl, …). */
  updateClient: (clientId: string, patch: Partial<Client>) => void;
  /** Create a new client from the editable scalar fields. */
  addClient: (data: NewClientInput) => void;
  /** Remove a client. */
  deleteClient: (clientId: string) => void;
  /** Upload a client logo and store its URL on the client. */
  uploadClientLogo: (clientId: string, file: File) => Promise<void>;
  /** Upload a file for a task; returns the attachment metadata to append. */
  uploadTaskAttachment: (taskId: string, file: File) => Promise<TaskAttachment>;
}

/** Small non-crypto id for client-generated records (links, attachments). */
function makeId(): string {
  return (
    globalThis.crypto?.randomUUID?.() ?? `id-${Date.now()}-${Math.random().toString(36).slice(2)}`
  );
}

/** The editable scalar fields used when creating a client from the table view. */
export type NewClientInput = Partial<
  Pick<Client, 'name' | 'initials' | 'website' | 'contactEmail' | 'phone' | 'notes'>
>;

const AVATAR_GRADIENTS = [
  'linear-gradient(135deg, #6d5efc, #8b7bff)',
  'linear-gradient(135deg, #ff7a59, #ff9f7a)',
  'linear-gradient(135deg, #2dd4bf, #34d399)',
  'linear-gradient(135deg, #f472b6, #fb7185)',
  'linear-gradient(135deg, #38bdf8, #60a5fa)',
  'linear-gradient(135deg, #facc15, #fbbf24)',
];

/** Deterministically pick an avatar gradient from a seed string. */
function pickGradient(seed: string): string {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  return AVATAR_GRADIENTS[h % AVATAR_GRADIENTS.length];
}

/** Derive up-to-two-letter initials from a client name. */
function deriveInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
}

/** Next `order` value (append to the end of the grid). */
function nextOrder(clients: Client[]): number {
  return clients.reduce((max, c) => Math.max(max, c.order), -1) + 1;
}

/** Next Lineup position (append to the end of the Lineup). */
function nextLineupOrder(tasks: Task[]): number {
  return tasks.reduce((max, t) => (t.inLineup ? Math.max(max, t.lineupOrder ?? 0) : max), 0) + 1;
}

/** Reassign `order` to match the given id sequence. */
function applyOrder(clients: Client[], orderedIds: string[]): Client[] {
  const byId = new Map(clients.map((c) => [c.id, c]));
  const reordered = orderedIds
    .map((id, index) => {
      const client = byId.get(id);
      return client ? { ...client, order: index } : undefined;
    })
    .filter((c): c is Client => c !== undefined);
  // Keep any clients not present in orderedIds (defensive) appended in place.
  const seen = new Set(orderedIds);
  const rest = clients.filter((c) => !seen.has(c.id));
  return [...reordered, ...rest];
}

function joinClientsAndTasks(clients: Client[], tasks: Task[]): ClientWithTasks[] {
  const byClient = new Map<string, Task[]>();
  for (const task of tasks) {
    const list = byClient.get(task.clientId) ?? [];
    list.push(task);
    byClient.set(task.clientId, list);
  }
  return [...clients]
    .sort((a, b) => a.order - b.order)
    .map((client) => ({ ...client, tasks: byClient.get(client.id) ?? [] }));
}

/** Seed-backed implementation: everything lives in component state. */
function useSeedData(): StudioData {
  const [clients, setClients] = useState<Client[]>(seedClients);
  const [tasks, setTasks] = useState<Task[]>(seedTasks);
  const [triage, setTriage] = useState<TriageItem[]>(seedTriage);

  const toggleTask = useCallback((taskId: string) => {
    setTasks((prev) =>
      prev.map((t) => {
        if (t.id !== taskId) return t;
        const done = !t.done;
        return {
          ...t,
          done,
          status: done ? 'done' : 'todo',
          // Completing a task clears it out of the Lineup automatically —
          // no need to also "X" it out by hand.
          ...(done ? { inLineup: false, lineupOrder: undefined } : {}),
        };
      }),
    );
  }, []);

  const addTask = useCallback((clientId: string, title: string) => {
    const trimmed = title.trim();
    if (!trimmed) return;
    const task: Task = {
      id: `n${Date.now()}`,
      clientId,
      title: trimmed,
      due: '',
      dueLabel: '',
      projectId: '',
      est: '',
      status: 'todo',
      notes: '',
      links: '',
      ai: false,
      done: false,
      createdAt: Date.now(),
    };
    setTasks((prev) => [task, ...prev]);
  }, []);

  const captureTriage = useCallback((title: string) => {
    const trimmed = title.trim();
    if (!trimmed) return;
    setTriage((prev) => [
      {
        id: `cap${Date.now()}`,
        title: trimmed,
        source: 'Captured',
        sourceColor: '#7ccaaa',
        age: 'just now',
        resolved: false,
        createdAt: Date.now(),
      },
      ...prev,
    ]);
  }, []);

  const dismissTriage = useCallback((itemId: string) => {
    setTriage((prev) => prev.filter((t) => t.id !== itemId));
  }, []);

  const assignTriageToClient = useCallback(
    (itemId: string, clientId: string, projectId: string, title: string) => {
      const trimmed = title.trim();
      if (!clientId || !trimmed) return;
      const source = triage.find((t) => t.id === itemId);
      const task: Task = {
        id: `n${Date.now()}`,
        clientId,
        title: trimmed,
        due: '',
        dueLabel: '',
        projectId: projectId || '',
        est: '',
        status: 'todo',
        notes: source?.notes ?? '',
        done: false,
        createdAt: Date.now(),
        ...(source?.attachments?.length ? { attachments: source.attachments } : {}),
        ...(source?.sourceRef ? { source: 'gmail', sourceId: source.sourceRef } : {}),
      };
      setTasks((prev) => [task, ...prev]);
      setTriage((prev) => prev.filter((t) => t.id !== itemId));
    },
    [triage],
  );

  const reorderClients = useCallback((orderedIds: string[]) => {
    setClients((prev) => applyOrder(prev, orderedIds));
  }, []);

  const updateTask = useCallback((taskId: string, patch: Partial<Task>) => {
    setTasks((prev) => prev.map((t) => (t.id === taskId ? { ...t, ...patch } : t)));
  }, []);

  const toggleLineup = useCallback((taskId: string) => {
    setTasks((prev) => {
      const order = nextLineupOrder(prev);
      return prev.map((t) =>
        t.id === taskId
          ? t.inLineup
            ? { ...t, inLineup: false, lineupOrder: undefined }
            : { ...t, inLineup: true, lineupOrder: order }
          : t,
      );
    });
  }, []);

  const reorderLineup = useCallback((orderedTaskIds: string[]) => {
    setTasks((prev) => {
      const orderById = new Map(orderedTaskIds.map((id, index) => [id, index]));
      return prev.map((t) =>
        orderById.has(t.id) ? { ...t, lineupOrder: orderById.get(t.id) } : t,
      );
    });
  }, []);

  const clearLineup = useCallback(() => {
    setTasks((prev) =>
      prev.map((t) => (t.inLineup ? { ...t, inLineup: false, lineupOrder: undefined } : t)),
    );
  }, []);

  const deleteTask = useCallback((taskId: string) => {
    setTasks((prev) => prev.filter((t) => t.id !== taskId));
  }, []);

  const updateClient = useCallback((clientId: string, patch: Partial<Client>) => {
    setClients((prev) => prev.map((c) => (c.id === clientId ? { ...c, ...patch } : c)));
  }, []);

  const addClient = useCallback((data: NewClientInput) => {
    const name = (data.name ?? '').trim();
    if (!name) return;
    setClients((prev) => [
      ...prev,
      {
        id: `c${Date.now()}`,
        name,
        initials: data.initials?.trim() || deriveInitials(name),
        avatarBg: pickGradient(name),
        projects: [],
        order: nextOrder(prev),
        website: data.website?.trim() || undefined,
        contactEmail: data.contactEmail?.trim() || undefined,
        phone: data.phone?.trim() || undefined,
        notes: data.notes?.trim() || undefined,
      },
    ]);
  }, []);

  const deleteClient = useCallback((clientId: string) => {
    setClients((prev) => prev.filter((c) => c.id !== clientId));
  }, []);

  const uploadClientLogo = useCallback(async (clientId: string, file: File) => {
    // Offline mode: use a local object URL for preview.
    const url = URL.createObjectURL(file);
    setClients((prev) => prev.map((c) => (c.id === clientId ? { ...c, logoUrl: url } : c)));
  }, []);

  const uploadTaskAttachment = useCallback(async (_taskId: string, file: File) => {
    // Offline mode: use a local object URL for preview.
    return {
      id: makeId(),
      name: file.name,
      url: URL.createObjectURL(file),
      size: file.size,
      contentType: file.type,
    } satisfies TaskAttachment;
  }, []);

  const joined = useMemo(() => joinClientsAndTasks(clients, tasks), [clients, tasks]);

  return {
    clients: joined,
    triage,
    loading: false,
    toggleTask,
    addTask,
    captureTriage,
    dismissTriage,
    assignTriageToClient,
    reorderClients,
    updateTask,
    toggleLineup,
    reorderLineup,
    clearLineup,
    deleteTask,
    updateClient,
    addClient,
    deleteClient,
    uploadClientLogo,
    uploadTaskAttachment,
  };
}

/** Firestore-backed implementation with real-time listeners. */
function useLiveData(): StudioData {
  const [clients, setClients] = useState<Client[]>([]);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [triage, setTriage] = useState<TriageItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!db) return;
    const unsubClients = onSnapshot(
      query(collection(db, 'clients'), orderBy('order')),
      (snap) => {
        setClients(snap.docs.map((d) => ({ id: d.id, ...d.data() }) as Client));
        setLoading(false);
      },
    );
    const unsubTasks = onSnapshot(collection(db, 'tasks'), (snap) => {
      setTasks(snap.docs.map((d) => ({ id: d.id, ...d.data() }) as Task));
    });
    const unsubTriage = onSnapshot(
      query(collection(db, 'triage'), where('resolved', '==', false)),
      (snap) => {
        setTriage(snap.docs.map((d) => ({ id: d.id, ...d.data() }) as TriageItem));
      },
    );
    return () => {
      unsubClients();
      unsubTasks();
      unsubTriage();
    };
  }, []);

  const toggleTask = useCallback(
    (taskId: string) => {
      if (!db) return;
      const task = tasks.find((t) => t.id === taskId);
      if (!task) return;
      const done = !task.done;
      void updateDoc(doc(db, 'tasks', taskId), {
        done,
        status: done ? 'done' : 'todo',
        // Completing a task clears it out of the Lineup automatically — no
        // need to also "X" it out by hand.
        ...(done ? { inLineup: false, lineupOrder: null } : {}),
      });
    },
    [tasks],
  );

  const addTask = useCallback((clientId: string, title: string) => {
    if (!db) return;
    const trimmed = title.trim();
    if (!trimmed) return;
    void addDoc(collection(db, 'tasks'), {
      clientId,
      title: trimmed,
      due: '',
      dueLabel: '',
      dueRank: DUE_RANK[''],
      projectId: '',
      est: '',
      status: 'todo',
      notes: '',
      links: '',
      ai: false,
      done: false,
      createdAt: serverTimestamp(),
    });
  }, []);

  const captureTriage = useCallback((title: string) => {
    if (!db) return;
    const trimmed = title.trim();
    if (!trimmed) return;
    void addDoc(collection(db, 'triage'), {
      title: trimmed,
      source: 'Captured',
      sourceColor: '#7ccaaa',
      age: 'just now',
      resolved: false,
      createdAt: serverTimestamp(),
    });
  }, []);

  const dismissTriage = useCallback((itemId: string) => {
    if (!db) return;
    void updateDoc(doc(db, 'triage', itemId), { resolved: true });
  }, []);

  const assignTriageToClient = useCallback(
    (itemId: string, clientId: string, projectId: string, title: string) => {
      const database = db;
      if (!database) return;
      const trimmed = title.trim();
      if (!clientId || !trimmed) return;
      const source = triage.find((t) => t.id === itemId);
      const batch = writeBatch(database);
      const taskRef = doc(collection(database, 'tasks'));
      batch.set(taskRef, {
        clientId,
        title: trimmed,
        due: '',
        dueLabel: '',
        dueRank: DUE_RANK[''],
        projectId: projectId || '',
        est: '',
        status: 'todo',
        notes: source?.notes ?? '',
        done: false,
        createdAt: serverTimestamp(),
        ...(source?.attachments?.length ? { attachments: source.attachments } : {}),
        ...(source?.sourceRef ? { source: 'gmail', sourceId: source.sourceRef } : {}),
      });
      batch.update(doc(database, 'triage', itemId), { resolved: true });
      void batch.commit();
    },
    [triage],
  );

  const reorderClients = useCallback((orderedIds: string[]) => {
    const database = db;
    if (!database) return;
    const batch = writeBatch(database);
    orderedIds.forEach((id, index) => {
      batch.update(doc(database, 'clients', id), { order: index });
    });
    // onSnapshot fires optimistically for local writes, so the grid reorders
    // immediately without waiting for the server round-trip.
    void batch.commit();
  }, []);

  const updateTask = useCallback((taskId: string, patch: Partial<Task>) => {
    if (!db) return;
    void updateDoc(doc(db, 'tasks', taskId), patch);
  }, []);

  const toggleLineup = useCallback(
    (taskId: string) => {
      if (!db) return;
      const task = tasks.find((t) => t.id === taskId);
      if (!task) return;
      void updateDoc(
        doc(db, 'tasks', taskId),
        task.inLineup
          ? { inLineup: false, lineupOrder: null }
          : { inLineup: true, lineupOrder: nextLineupOrder(tasks) },
      );
    },
    [tasks],
  );

  const reorderLineup = useCallback((orderedTaskIds: string[]) => {
    const database = db;
    if (!database) return;
    const batch = writeBatch(database);
    orderedTaskIds.forEach((id, index) => {
      batch.update(doc(database, 'tasks', id), { lineupOrder: index });
    });
    void batch.commit();
  }, []);

  const clearLineup = useCallback(() => {
    const database = db;
    if (!database) return;
    const inLineup = tasks.filter((t) => t.inLineup);
    if (!inLineup.length) return;
    const batch = writeBatch(database);
    inLineup.forEach((t) =>
      batch.update(doc(database, 'tasks', t.id), { inLineup: false, lineupOrder: null }),
    );
    void batch.commit();
  }, [tasks]);

  const deleteTask = useCallback((taskId: string) => {
    if (!db) return;
    void deleteDoc(doc(db, 'tasks', taskId));
  }, []);

  const updateClient = useCallback((clientId: string, patch: Partial<Client>) => {
    if (!db) return;
    void updateDoc(doc(db, 'clients', clientId), patch);
  }, []);

  const addClient = useCallback(
    (data: NewClientInput) => {
      if (!db) return;
      const name = (data.name ?? '').trim();
      if (!name) return;
      void addDoc(collection(db, 'clients'), {
        name,
        initials: data.initials?.trim() || deriveInitials(name),
        avatarBg: pickGradient(name),
        projects: [],
        order: nextOrder(clients),
        website: data.website?.trim() ?? '',
        contactEmail: data.contactEmail?.trim() ?? '',
        phone: data.phone?.trim() ?? '',
        notes: data.notes?.trim() ?? '',
        createdAt: serverTimestamp(),
      });
    },
    [clients],
  );

  const deleteClient = useCallback((clientId: string) => {
    if (!db) return;
    void deleteDoc(doc(db, 'clients', clientId));
  }, []);

  const uploadClientLogo = useCallback(async (clientId: string, file: File) => {
    if (!db || !storage) return;
    const ext = file.name.split('.').pop() || 'png';
    const objectRef = storageRef(storage, `clients/${clientId}/logo-${Date.now()}.${ext}`);
    await uploadBytes(objectRef, file);
    const url = await getDownloadURL(objectRef);
    await updateDoc(doc(db, 'clients', clientId), { logoUrl: url });
  }, []);

  const uploadTaskAttachment = useCallback(async (taskId: string, file: File) => {
    if (!storage) throw new Error('Storage is not configured');
    const objectRef = storageRef(storage, `tasks/${taskId}/${Date.now()}-${file.name}`);
    await uploadBytes(objectRef, file);
    const url = await getDownloadURL(objectRef);
    return {
      id: makeId(),
      name: file.name,
      url,
      size: file.size,
      contentType: file.type,
    } satisfies TaskAttachment;
  }, []);

  const joined = useMemo(() => joinClientsAndTasks(clients, tasks), [clients, tasks]);

  return {
    clients: joined,
    triage,
    loading,
    toggleTask,
    addTask,
    captureTriage,
    dismissTriage,
    assignTriageToClient,
    reorderClients,
    updateTask,
    toggleLineup,
    reorderLineup,
    clearLineup,
    deleteTask,
    updateClient,
    addClient,
    deleteClient,
    uploadClientLogo,
    uploadTaskAttachment,
  };
}

/**
 * Studio Board data source. Uses live Firestore when configured
 * (`VITE_USE_FIRESTORE=true` + valid config); otherwise falls back to seed
 * data. The two hooks are called unconditionally per the rules of hooks — the
 * `useFirestore` flag is constant for the lifetime of the app.
 */
export function useStudioData(): StudioData {
  const live = useLiveData();
  const seed = useSeedData();
  return useFirestore && db ? live : seed;
}
